import { css, cx } from "@emotion/css";

type RuleDefinition = Record<string, any>;
type StylesSheet = Record<
  string,
  RuleDefinition | ((props: any) => RuleDefinition)
>;
type StylesInput = StylesSheet | ((theme: any) => StylesSheet);

// Réplique le comportement actuel en production : aucun ThemeProvider
// react-jss n'est monté (src/index.tsx n'utilise que celui de
// @mui/material/styles), donc le thème injecté par react-jss vaut
// toujours {} par défaut. On préserve ce comportement à l'identique
// (bug inclus, cf. SelectList.styles.tsx) plutôt que de le corriger.
const REACT_JSS_UNPROVIDED_THEME = {};

// jss-plugin-expand ajoute "px" aux nombres bruts d'un tableau shorthand
// (padding: [8, 16] => "8px 16px") avant de les joindre — sans cette
// conversion, un nombre brut joint en string ("8 16") ne bénéficie plus de
// l'ajout automatique d'unité que fait normalement stylis sur une valeur
// numérique isolée, et produirait du CSS invalide.
const withPxUnit = (v: unknown): string =>
  typeof v === "number" && Number.isFinite(v) ? `${v}px` : String(v);

function expandArrayShorthand(value: unknown): unknown {
  if (!Array.isArray(value)) return value;
  // Réplique jss-plugin-expand : tableau imbriqué => valeurs séparées par
  // virgule (listes multi-valeurs type transition), tableau plat =>
  // valeurs séparées par espace (shorthand box-model : padding/margin/
  // border-width/etc.). @emotion/css ne fait pas cette expansion lui-même
  // (un array brut produit du CSS invalide, silencieusement).
  if (value.some(Array.isArray)) {
    return value
      .map((v) =>
        Array.isArray(v) ? v.map(withPxUnit).join(" ") : withPxUnit(v)
      )
      .join(", ");
  }
  return value.map(withPxUnit).join(" ");
}

function normalizeRule(rule: RuleDefinition, props: any): RuleDefinition {
  const out: RuleDefinition = {};
  for (const [key, raw] of Object.entries(rule)) {
    if (key === "composes") continue; // géré séparément dans useStyles
    const value = typeof raw === "function" ? raw(props) : raw;
    out[key] =
      value && typeof value === "object" && !Array.isArray(value)
        ? normalizeRule(value as RuleDefinition, props) // sélecteur imbriqué / @media
        : expandArrayShorthand(value);
  }
  return out;
}

/**
 * Remplaçant compatible de `createUseStyles` de react-jss, construit sur
 * @emotion/css. Accepte les mêmes formes d'entrée (objet statique,
 * fonction (theme) => objet, règles individuelles en fonction de props,
 * `composes: "$autreRegle"`, raccourci array JSS) et retourne un hook
 * produisant le même objet `classes` que react-jss.
 */
export function createUseStyles(input: StylesInput) {
  return function useStyles(props: any = {}): Record<string, string> {
    const sheet =
      typeof input === "function" ? input(REACT_JSS_UNPROVIDED_THEME) : input;
    const resolvedByRule: Record<string, string> = {};
    const classes: Record<string, string> = {};

    for (const [ruleName, rawRule] of Object.entries(sheet)) {
      const ruleDef = typeof rawRule === "function" ? rawRule(props) : rawRule;
      // "label" fait porter le nom de la règle JSS d'origine dans la classe
      // générée (ex. "css-tnx5ko-categoryMain" au lieu de "css-tokvmb") :
      // sans ça, les sélecteurs Playwright existants en [class*="ruleName"]
      // (qui reposaient sur la convention de nommage de react-jss) ne
      // matchent plus rien avec les classes purement hashées d'Emotion.
      const own = css({ ...normalizeRule(ruleDef, props), label: ruleName });
      const composesRaw = (ruleDef as any).composes as string | undefined;

      classes[ruleName] = composesRaw
        ? cx(
            ...composesRaw
              .trim()
              .split(/\s+/)
              .map((t) => t.replace(/^\$/, ""))
              .map((name) => resolvedByRule[name])
              .filter(Boolean),
            own
          )
        : own;

      resolvedByRule[ruleName] = classes[ruleName];
    }

    return classes;
  };
}
